import React, { useEffect, useRef, useState, useCallback } from 'react';
import TrackToggles from './TrackToggles';
import {
  PALETTE,
  drawEmptyState,
  parseMeasures,
  pitchToName,
  roundedRect,
  useElementWidth,
  useJsonResource,
  usePlayhead,
} from './pianoRollShared';
import './PianoRoll.css';

const TRACK_COLORS = {
  melody:  { fill: '#e34673', context: '#e0b1bf', label: 'Melody' },
  harmony: { fill: '#341b63', context: '#a695c4', label: 'Harmony' },
};

const TRACK_ORDER = ['harmony', 'melody'];

const INFILL_ACCENT = '#1e40af';
const INFILL_TINT_EVEN = '#eaf0fb';
const INFILL_TINT_ODD = '#f2f6fd';

const getMeasureIndex = (noteTime, downbeats) => {
  for (let i = downbeats.length - 1; i >= 0; i -= 1) {
    if (noteTime >= downbeats[i] - 0.001) return i;
  }
  return 0;
};

// Expected JSON shape:
//   { "melody": { "notes": [...] }, "harmony": { "notes": [...] },
//     "click": { "notes": [...] }, "bpm": float, "key": string }
//
// Unlike the multitrack roll, lead sheet audio is not silence-trimmed, so note
// times map straight onto the audio clock with no shift.
const PianoRoll = ({ jsonUrl, audioRef, infillSpan }) => {
  const canvasRef = useRef(null);
  const playheadRef = useRef(null);
  const wrapperRef = useRef(null);
  const layoutRef = useRef(null);

  const { data, error } = useJsonResource(jsonUrl);
  const width = useElementWidth(wrapperRef);
  const [visibleTracks, setVisibleTracks] = useState({ melody: true, harmony: true });

  const drawPianoRoll = useCallback(() => {
    if (!data || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const allNotes = [];
    for (const track of TRACK_ORDER) {
      if (!visibleTracks[track] || !data[track]) continue;
      for (const note of data[track].notes) {
        allNotes.push({ ...note, track });
      }
    }

    if (allNotes.length === 0) {
      drawEmptyState(canvas, ctx, dpr, 'No parts selected');
      layoutRef.current = null;
      return;
    }

    const downbeats = parseMeasures(data.click?.notes || []);
    const numMeasures = downbeats.length;

    const minPitch = Math.min(...allNotes.map((n) => n.pitch)) - 1;
    const maxPitch = Math.max(...allNotes.map((n) => n.pitch)) + 1;
    const maxTime = Math.max(...allNotes.map((n) => n.time + Math.max(n.duration, 0.08)));
    const pitchRange = maxPitch - minPitch + 1;

    const canvasWidth = canvas.offsetWidth;
    const noteHeight = Math.max(4, Math.min(10, 160 / pitchRange));
    const height = Math.max(80, pitchRange * noteHeight + 48);
    const leftPad = 36;
    const topPad = 24;
    const drawWidth = canvasWidth - leftPad - 4;

    layoutRef.current = { leftPad, drawWidth, maxTime, topPad, height };

    canvas.width = canvasWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const timeToX = (t) => leftPad + (t / maxTime) * drawWidth;

    ctx.fillStyle = PALETTE.background;
    ctx.fillRect(0, 0, canvasWidth, height);

    for (let i = 0; i < numMeasures; i += 1) {
      const x0 = timeToX(downbeats[i]);
      const x1 = i + 1 < numMeasures ? timeToX(downbeats[i + 1]) : timeToX(maxTime);
      const isInSpan = infillSpan && i >= infillSpan.start && i < infillSpan.end;

      if (isInSpan) {
        ctx.fillStyle = i % 2 === 0 ? INFILL_TINT_EVEN : INFILL_TINT_ODD;
      } else {
        ctx.fillStyle = i % 2 === 0 ? PALETTE.measureEven : PALETTE.measureOdd;
      }
      ctx.fillRect(x0, topPad, x1 - x0, height - topPad);
    }

    if (infillSpan && downbeats.length > 0) {
      const spanStartX = infillSpan.start < downbeats.length
        ? timeToX(downbeats[infillSpan.start])
        : null;
      const spanEndX = infillSpan.end < downbeats.length
        ? timeToX(downbeats[infillSpan.end])
        : timeToX(maxTime);

      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = INFILL_ACCENT;
      ctx.lineWidth = 1.5;

      if (spanStartX !== null) {
        ctx.beginPath();
        ctx.moveTo(spanStartX, topPad);
        ctx.lineTo(spanStartX, height);
        ctx.stroke();
      }
      if (spanEndX !== null) {
        ctx.beginPath();
        ctx.moveTo(spanEndX, topPad);
        ctx.lineTo(spanEndX, height);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    for (let i = 0; i < numMeasures; i += 1) {
      const x = timeToX(downbeats[i]);
      ctx.strokeStyle = PALETTE.measureLine;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, topPad);
      ctx.lineTo(x, height);
      ctx.stroke();

      const nextX = i + 1 < numMeasures ? timeToX(downbeats[i + 1]) : timeToX(maxTime);
      const isInSpan = infillSpan && i >= infillSpan.start && i < infillSpan.end;
      ctx.fillStyle = isInSpan ? INFILL_ACCENT : PALETTE.measureLabel;
      ctx.font = isInSpan
        ? 'bold 10px Inter, -apple-system, sans-serif'
        : '10px Inter, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${i + 1}`, (x + nextX) / 2, topPad - 8);
    }

    for (const beat of (data.click?.notes || []).filter((n) => n.pitch === 32)) {
      const x = timeToX(beat.time);
      ctx.strokeStyle = PALETTE.beatLine;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, topPad);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let p = minPitch; p <= maxPitch; p += 1) {
      if (p % 12 !== 0) continue;
      const y = topPad + (maxPitch - p) * noteHeight;
      ctx.strokeStyle = PALETTE.pitchLine;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(leftPad, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
      ctx.fillStyle = PALETTE.pitchLabel;
      ctx.font = '9px Inter, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(pitchToName(p), leftPad - 4, y + 3);
    }

    for (const track of TRACK_ORDER) {
      if (!visibleTracks[track] || !data[track]) continue;
      const colors = TRACK_COLORS[track];

      for (const note of data[track].notes) {
        const measureIdx = getMeasureIndex(note.time, downbeats);
        const isGenerated = !infillSpan
          || (measureIdx >= infillSpan.start && measureIdx < infillSpan.end);

        const x = timeToX(note.time);
        const y = topPad + (maxPitch - note.pitch) * noteHeight;
        const w = Math.max(3, (Math.max(note.duration, 0.06) / maxTime) * drawWidth);
        const h = Math.max(3, noteHeight - 1);

        ctx.fillStyle = isGenerated ? colors.fill : colors.context;
        ctx.globalAlpha = isGenerated ? 0.85 : 0.45;
        roundedRect(ctx, x, y, w, h, 2);
        ctx.globalAlpha = 1;
      }
    }
  }, [data, visibleTracks, infillSpan]);

  useEffect(() => {
    drawPianoRoll();
  }, [drawPianoRoll, width]);

  usePlayhead(audioRef, layoutRef, playheadRef);

  const toggleTrack = useCallback((key) => {
    setVisibleTracks((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setAllTracks = useCallback((value) => {
    setVisibleTracks({ melody: value, harmony: value });
  }, []);

  if (error) {
    return <div className="piano-roll-error">Visualization unavailable</div>;
  }

  if (!data) {
    return <div className="piano-roll-loading" />;
  }

  const groups = TRACK_ORDER
    .filter((track) => data[track])
    .map((track) => ({
      key: track,
      label: TRACK_COLORS[track].label,
      title: TRACK_COLORS[track].label,
      color: TRACK_COLORS[track].fill,
    }));

  return (
    <div className="piano-roll-wrapper">
      <TrackToggles
        groups={groups}
        visible={visibleTracks}
        onToggle={toggleTrack}
        onSetAll={setAllTracks}
      />
      <div className="piano-roll-canvas-wrapper" ref={wrapperRef}>
        <canvas ref={canvasRef} className="piano-roll-canvas" />
        <div ref={playheadRef} className="piano-roll-playhead" />
      </div>
    </div>
  );
};

export default PianoRoll;
